'use client';

import React, { useState } from 'react';
import { Toaster } from 'sonner';
import StarField from './StarField';
import AppHeader from './AppHeader';
import InputForm from './InputForm';
import Step2Questionnaire from './Step2Questionnaire';
import Step2Report from './Step2Report';
import { analyseTrader, type TraderProfile } from '../utils/numerology';
import type { Step2Result } from '../utils/step2Scoring';
import { applyStep1CrossRef } from '../utils/step2Scoring';

type AppStep = 'landing' | 'questionnaire' | 'result';

export interface LandingData {
  name: string;
  email: string;
}

export default function CompatibilityReaderClient() {
  const [appStep, setAppStep] = useState<AppStep>('landing');
  const [landingData, setLandingData] = useState<LandingData | null>(null);
  const [step1Result, setStep1Result] = useState<TraderProfile | null>(null);
  const [step2Result, setStep2Result] = useState<Step2Result | null>(null);

  const handleLandingComplete = (data: LandingData) => {
    setLandingData(data);
    setAppStep('questionnaire');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStep2Complete = (s2Result: Step2Result, dob: string) => {
    // Run I Ching analysis using the DOB collected at end of questionnaire
    const profile = analyseTrader({ name: s2Result.traderName, dob });
    // Now that we have step1 emotions, recompute the cross-reference
    const step1EmotionNames = profile.emotions.map(e => e.emotion);
    const updatedResult = applyStep1CrossRef(s2Result, step1EmotionNames);
    setStep1Result(profile);
    setStep2Result(updatedResult);
    setAppStep('result');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReset = () => {
    setLandingData(null);
    setStep1Result(null);
    setStep2Result(null);
    setAppStep('landing');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToLanding = () => {
    setAppStep('landing');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-950 cosmic-gradient relative overflow-x-hidden">
      <StarField />
      <Toaster position="bottom-right" theme="dark" richColors />
      <div className="relative z-10">
        <AppHeader />
        <main className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">

          {/* Step 1 — Landing Form */}
          {appStep === 'landing' && (
            <>
              <HeroSection />
              <InputForm
                onComplete={handleLandingComplete}
              />
              <WhatYouWillDiscover />
            </>
          )}

          {/* Step 2 — Questionnaire */}
          {appStep === 'questionnaire' && landingData && (
            <Step2Questionnaire
              userName={landingData.name}
              onComplete={handleStep2Complete}
              onBack={handleBackToLanding}
            />
          )}

          {/* Result */}
          {appStep === 'result' && step2Result && step1Result && (
            <Step2Report
              result={step2Result}
              step1Result={step1Result}
              onBack={() => setAppStep('questionnaire')}
              onRestartStep1={handleReset}
            />
          )}

        </main>
      </div>
    </div>
  );
}

function HeroSection() {
  return (
    <div className="text-center pt-12 pb-10 animate-fade-in">
      <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-300 text-sm font-medium mb-6">
        <span className="text-base">☯</span>
        <span>Trader Cognitive Bias Diagnosis</span>
      </div>

      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
        Know Your{' '}
        <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
          Trading Mind
        </span>
      </h1>
      <p className="text-slate-300 text-lg max-w-2xl mx-auto leading-relaxed mb-8">
        A structured two-step assessment that reveals your inborn behavioural patterns and confirms which emotional biases are currently active in your trading.
      </p>

      <div className="max-w-3xl mx-auto mb-10">
        <p className="text-slate-500 text-xs uppercase tracking-widest font-medium mb-5">
          2-Step Trader Cognitive Bias Diagnosis
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
          <div className="relative rounded-2xl p-5 bg-violet-500/10 border border-violet-500/30">
            <div className="absolute -top-3 left-5">
              <span className="bg-violet-600 text-white text-xs font-bold px-3 py-1 rounded-full">You are here</span>
            </div>
            <div className="flex items-center gap-3 mb-3 mt-2">
              <div className="w-8 h-8 rounded-full bg-violet-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">1</div>
              <p className="text-violet-300 text-xs font-semibold uppercase tracking-wide">Registration</p>
            </div>
            <p className="text-white text-sm font-semibold mb-1">Enter Your Details</p>
            <p className="text-slate-400 text-xs leading-relaxed">
              Provide your name, email and access password to begin the assessment.
            </p>
          </div>

          <div className="rounded-2xl p-5 bg-slate-800/40 border border-slate-700/40">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-slate-300 text-sm font-bold flex-shrink-0">2</div>
              <p className="text-slate-500 text-xs font-semibold uppercase tracking-wide">Assessment</p>
            </div>
            <p className="text-slate-300 text-sm font-semibold mb-1">Behavioural Assessment Questionnaire</p>
            <p className="text-slate-500 text-xs leading-relaxed">
              Complete the structured questionnaire. Your I Ching analysis is generated from your date of birth at the end.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto rounded-2xl bg-slate-900/60 border border-slate-700/50 p-6 text-left">
        <p className="text-slate-400 text-xs uppercase tracking-widest font-medium mb-3">Before you begin — important context</p>
        <p className="text-slate-300 text-sm leading-relaxed mb-3">
          The output of this analysis is <span className="text-white font-medium">not a judgment of your intelligence or skill</span>. It is a map of your inborn cognitive architecture — the same way a blood test reveals your physiology, not your worth.
        </p>
        <p className="text-slate-300 text-sm leading-relaxed">
          Every trader has patterns. The ones who perform consistently are not those without biases — they are the ones who <span className="text-violet-300 font-medium">know their biases and have built systems around them</span>.
        </p>
      </div>
    </div>
  );
}

function WhatYouWillDiscover() {
  const items = [
    { icon: '☯', label: 'Main Character Number', desc: 'Your core trading archetype derived from the I Ching triangle' },
    { icon: '🔢', label: '13 Behaviour Codes', desc: 'Inborn patterns across career, wealth, relationships & subconscious' },
    { icon: '⚡', label: '5 Elements Distribution', desc: 'Metal, Water, Fire, Wood, Earth — your energy balance and wealth score' },
    { icon: '🧠', label: 'Cognitive Bias Flags', desc: 'Fear, Greed, Ego, Impatience, Hope — which patterns are active in you' },
    { icon: '📊', label: 'Trader Category', desc: 'Category 1 (aligned), 2 (fixable), or 3 (needs restructuring)' },
    { icon: '🎯', label: 'Trading Blindspots', desc: 'Specific behaviours to watch for in your live trading decisions' },
  ];

  return (
    <div className="mt-16 animate-fade-in">
      <p className="text-center text-slate-500 text-sm uppercase tracking-widest font-medium mb-8">
        What this assessment reveals
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(item => (
          <div key={`item-${item.label}`} className="dimension-card rounded-xl p-4">
            <div className="text-2xl mb-3">{item.icon}</div>
            <p className="text-slate-200 text-sm font-semibold mb-1">{item.label}</p>
            <p className="text-slate-500 text-xs leading-relaxed">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}